/**
 * Consigning a work to a gallery, from the owner's — or the agent's — side.
 *
 * The last server-complete, client-absent gap. `ondo.offer_consignment()` has
 * existed since 20260902090000 and the gallery's inventory has said "an artist
 * or estate consigns a work from its own record" since it shipped — and there
 * was no record with a control on it. Gallery inventory could only be filled
 * by a seed script.
 *
 * ONE COMPONENT, MOUNTED WHERE THE WORK IS. On the work's own record, beside
 * the agents panel, because that is where the gallery's copy says the act
 * lives; and on the agent's Share Codes screen, under "Agree a consignment",
 * because that is the half of the label the rail promised. The estate rail
 * may mount it too — the definer admits an owner, and an estate that
 * registered its own work is one.
 */
export const CONSIGN_COPY = {
  title: "Consignment",
  loading: "Reading…",

  /** The panel when nothing is consigned and the reader may consign. */
  none: "Not with a gallery.",
  noneDetail:
    "Offering it to one puts the work in their inventory once they accept, on the terms you set here. Until they do, the offer stands and you may withdraw it.",

  /** The panel when nothing is consigned and the reader may not consign. */
  noneReadOnly: "Not with a gallery.",

  /** 'Offered to Hauser & Wirth (ONDO-6624) on 4 Feb 2026 — awaiting their answer'. */
  offeredTo: "Offered to",
  consignedTo: "With",
  awaiting: "awaiting their answer",
  since: "since",
  on: "on",

  terms: {
    arrangement: "Arrangement",
    basis: "Basis",
    asking: "Asking",
    floor: "Floor",
    note: "Note",
  },

  arrangement: {
    consignment: "Consignment",
    primary_representation: "Primary representation",
  },
  arrangementHelp: {
    consignment: "The gallery holds and may sell this work. Title stays with you until a sale.",
    primary_representation:
      "The gallery represents the artist on this work as a matter of standing, not a single placement.",
  },
  basis: {
    net_of_sale: "Net of sale",
    min_price_agreed: "Minimum price agreed",
    unspecified: "Unspecified",
  },

  offer: {
    open: "Offer to a gallery",
    title: "Offer this work to a gallery",
    gallery: "Gallery’s ONDO id",
    galleryPlaceholder: "ONDO-6624",
    galleryHelp:
      "The gallery gives you its id. The registry checks it is a gallery before the offer is sent, and says nothing about ids that are not.",
    checking: "Checking…",
    /** 'Hauser & Wirth' — the server's own name for an eligible id. */
    eligible: (name: string) => name,
    currency: "Currency",
    asking: "Asking price",
    askingHelp: "Optional. What you would like the work to sell for.",
    floor: "Floor",
    floorHelp: "Optional. The least you would accept. Never shown to a buyer.",
    note: "Note to the gallery",
    submit: "Send the offer",
    sending: "Sending…",
    cancel: "Cancel",
    /** Shown after a successful offer, in place of the form. */
    sent: "Offered. The gallery has been told, and the work appears in their inventory once they accept.",
  },

  recall: {
    /** Before acceptance: withdrawing an offer. After: recalling the work. */
    withdraw: "Withdraw the offer",
    recall: "Recall from the gallery",
    confirmWithdraw: "Yes, withdraw it",
    confirmRecall: "Yes, recall it",
    cancel: "Keep it",
    withdrawNote: "Ends the offer before the gallery answers. They are told, and the row stays on the record marked withdrawn.",
    recallNote:
      "Ends the consignment. The work leaves the gallery’s inventory and any agent they appointed on it is stood down in the same statement. The row stays on the record.",
  },

  /**
   * The owner's switch for where the work is — OND-88. Drawn only on an
   * accepted consignment the reader may act on; the sentence under it is the
   * whole disclosure decision, so a reader knows what the box does before
   * ticking it.
   */
  location: {
    label: "Let the gallery see where it is",
    on: "The gallery can see this work’s location, and may record a move while it holds the work. Their view freezes at a sale.",
    off: "The gallery cannot see where this work is. Tick to let them, for this consignment only.",
    saving: "Saving…",
    /** Ticked on an unanswered offer: honest about when it takes effect. */
    pending: "Takes effect once the gallery accepts.",
  },

  /** Shown in place of the offer control, to a seat that may read and not act. */
  needsRung:
    "Offering this work to a gallery commits its owner, so it is theirs to do — or an agent’s at editor level or above.",

  errors: {
    invalidId: "That is not an ONDO id.",
    invalidMoney: "That is not an amount.",
    floorAboveAsking: "The floor cannot be above the asking price.",
    currencyNeeded: "Name a currency for the figures.",
    refused: "That work cannot be offered by this account.",
    throttled: "Too many checks this hour. Try again later.",
  },
} as const;
